import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Paper from '@mui/material/Paper';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import TabMode from '../Enums/TabMode';
import ItemsTab from '../Tabs/ItemsTab/ItemsTab';
import DocumentsTab from '../Tabs/DocumentsTab/DocumentsTab';
import SettingsTab from '../Tabs/SettingsTab/SettingsTab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faGear, faNoteSticky } from '@fortawesome/free-solid-svg-icons';
import { updateLastActivity } from '../Utils/LastActivity';
import type { UserContextProps } from '../Contexts/UserContext';
import UserContext from '../Contexts/UserContext';
import type { LocalStorageContextProps } from '../Contexts/LocalStorageContext';
import LocalStorageContext from '../Contexts/LocalStorageContext';
import { navBarHeight } from '../Constants';
import type { NavigateFunction} from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { parseNumberStrict } from 'shouldve-been-js';

type MainPageProps = {
    tabMode: TabMode,
    subView: string | null,
};

function MainPage(props: Readonly<MainPageProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);

    const navigate: NavigateFunction = useNavigate();

    const { itemId, treeNodeId } = useParams(); //dynamic id from url

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const mainRenderBody: JSX.Element = useMemo<JSX.Element>(() => {

        if (props.tabMode === TabMode.ITEMS) {
            return <ItemsTab openedId={itemId ?? null} />; 

        } else if (props.tabMode === TabMode.DOCUMENTS) {
            const numericTreeNodeId: number | null = parseNumberStrict(treeNodeId ?? null);
            return <DocumentsTab treeNodeId={numericTreeNodeId ?? 0} />;  //assumes node with id 0 is always the root 

        } else if (props.tabMode === TabMode.SETTINGS) {
            return <SettingsTab openedSubTab={props.subView} />; 

        } else { //should not happen
            return <ItemsTab openedId={null} />;
        }
    }, [itemId, props.subView, props.tabMode, treeNodeId]);

    const bottonNavigationValue: string = useMemo<string>(() => {

        if (props.tabMode === TabMode.ITEMS) {
            return '/items'; 

        } else if (props.tabMode === TabMode.DOCUMENTS) {
            return '/documents'; 

        } else if (props.tabMode === TabMode.SETTINGS) {
            return '/settings'; 

        } else { //should not happen
            return '/items';
        }
    }, [props.tabMode]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    return (
        <div>
            <div style={{
                height: windowSize[1] - navBarHeight,
                overflow: 'auto',
            }}>
                {mainRenderBody}
            </div>
            <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
                <BottomNavigation
                    showLabels
                    value={bottonNavigationValue}
                    onChange={(event: React.SyntheticEvent, newRoute: string): void => {
                        updateLastActivity(userContext, localStorageContext);
                        navigate(newRoute);
                    }}
                    style={{
                        height: navBarHeight,
                    }}
                >
                    <BottomNavigationAction value={'/items'} label='Items' icon={<FontAwesomeIcon icon={faNoteSticky} size='xl' />} />
                    <BottomNavigationAction value={'/documents'} label='Documents' icon={<FontAwesomeIcon icon={faFile} size='xl' />} />
                    <BottomNavigationAction value={'/settings'} label='Settings' icon={<FontAwesomeIcon icon={faGear} size='xl' />} />
                </BottomNavigation>
            </Paper>
        </div>
    );
}

export default MainPage;